import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircularProgress, Box, Typography, Alert } from "@mui/material";
import { useAuth } from "../../hooks/auth";
import { getApiBaseUrl } from "../../hooks/api";
import axios from "axios";

interface IntegrationAccessResponse {
  success: boolean;
  user: {
    id: number;
    name: string;
    email: string;
    roles: string[];
  };
  appointment: {
    id: number;
    date: string;
    status: number;
    patient: {
      id: number;
      name: string;
      userId: number;
    };
    doctor: {
      id: number;
      name: string;
      userId: number;
    };
  };
  type: "patient" | "doctor";
  accessToken: string;
  externalAppointmentId: string | null;
}

const VIDEO_CALL_PARTICIPANTS_KEY = "@maisSaudeVideoCallParticipants";
const MAIS_SAUDE_RETURN_KEY = "@maisSaudeVideoCallReturn";
const DEFAULT_MAIS_SAUDE_ORIGIN = process.env.REACT_APP_MAIS_SAUDE_URL || "https://app.mqssaude.com.br";

type MaisSaudeReturnConfig = {
  returnUrl: string | null;
  origin: string | null;
  vivaseAppointmentId: number;
  maisSaudeAppointmentId: string | null;
};

const parseMaisSaudeAppointmentId = (returnUrl: string | null) => {
  if (!returnUrl) {
    return null;
  }

  try {
    return new URL(returnUrl).searchParams.get("appointmentId");
  } catch {
    return null;
  }
};

export const IntegrationAccess = () => {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { signInWithToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateAndRedirect = async () => {
      if (!token) {
        setError("Token de acesso inválido");
        setLoading(false);
        return;
      }

      try {
        const baseUrl = getApiBaseUrl();
        const response = await axios.get<IntegrationAccessResponse>(
          `${baseUrl}/integration/access/${token}`
        );

        if (response.data.success) {
          const { user, appointment, type, accessToken } = response.data;

          // Store the URL token for later use (documents page)
          localStorage.setItem('@documentToken', token);
          sessionStorage.setItem(
            VIDEO_CALL_PARTICIPANTS_KEY,
            JSON.stringify({
              appointmentId: appointment.id,
              patientName: appointment.patient.name,
              doctorName: appointment.doctor.name,
              patientUserId: appointment.patient.userId,
              doctorUserId: appointment.doctor.userId,
            })
          );

          const integrationSearchParams = new URLSearchParams(location.search);
          const maisSaudeReturnUrl = integrationSearchParams.get("maisSaudeReturnUrl");
          const maisSaudeOrigin =
            integrationSearchParams.get("maisSaudeOrigin") || DEFAULT_MAIS_SAUDE_ORIGIN;
          const maisSaudeAppointmentId =
            response.data.externalAppointmentId ||
            parseMaisSaudeAppointmentId(maisSaudeReturnUrl);

          const returnConfig: MaisSaudeReturnConfig = {
            returnUrl: maisSaudeReturnUrl,
            origin: maisSaudeOrigin,
            vivaseAppointmentId: appointment.id,
            maisSaudeAppointmentId,
          };
          sessionStorage.setItem(MAIS_SAUDE_RETURN_KEY, JSON.stringify(returnConfig));

          // Sign in the user with the JWT token returned by the backend
          await signInWithToken(accessToken, user, type);

          // Redirect directly to video call
          navigate(`/video/${appointment.id}${location.search}`, { replace: true });
        } else {
          setError("Token de acesso inválido ou expirado");
        }
      } catch (err: any) {
        console.error("Error validating integration token:", err);
        setError(
          err.response?.data?.message ||
            "Erro ao validar token de acesso. Tente novamente."
        );
      } finally {
        setLoading(false);
      }
    };

    validateAndRedirect();
  }, [token, location.search, navigate, signInWithToken]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Validando acesso...</Typography>
        <Typography variant="body2" color="text.secondary">
          Aguarde enquanto preparamos sua consulta
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Se o problema persistir, entre em contato com o suporte.
        </Typography>
      </Box>
    );
  }

  return null;
};

export default IntegrationAccess;
